import { FC, useMemo } from "react";
import { Controller } from "react-hook-form";
import { PLATFORM_MODULES_OPTIONS } from "@miplatform/constants";
import {
  PLATFORM_MODULE_TO_FLAG_KEY,
  useFeatureFlags,
} from "@miplatform/feature-flags";
import { ITransferListItem, PlatformModules } from "@miplatform/types";
import { mdiLock } from "@mdi/js";
import { TransferList } from "@mibuildweb/components";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { DepartmentDetailsConfigViewProps } from "../../ConfigDepartments.types";

const DISABLED_MODULE_TOOLTIP =
  "Access to this module requires an additional subscription. Upgrade your plan to unlock this feature.";

export const DepartmentConfigModulesTabView: FC<
  DepartmentDetailsConfigViewProps
> = ({ form }) => {
  const { featureFlags } = useFeatureFlags();

  const availableModules: ITransferListItem[] = useMemo(
    () =>
      PLATFORM_MODULES_OPTIONS.map((module) => {
        const featureFlagKey =
          PLATFORM_MODULE_TO_FLAG_KEY[module.value as PlatformModules];
        const isEnabled = featureFlagKey
          ? featureFlags?.[featureFlagKey] !== false
          : true;

        return {
          ...module,
          key: module.value,
          title: module.label,
          disabled: !isEnabled,
          description: !isEnabled ? DISABLED_MODULE_TOOLTIP : undefined,
          addIcon: isEnabled ? undefined : mdiLock,
        };
      }).sort((a, b) => a.title.localeCompare(b.title)),
    [featureFlags]
  );

  return (
    <ConfigAccordionHeader title="Modules">
      <Controller
        control={form.control}
        name="modules"
        render={({ field }) => {
          const selectedValues: ITransferListItem[] = field.value
            .map((module: string) => ({
              key: module,
              title:
                PLATFORM_MODULES_OPTIONS.find(
                  (option) => option.value === module
                )?.label || module,
              value: module,
            }))
            .sort((a, b) => a.title.localeCompare(b.title));

          const handleAddItem = (key: string) => {
            const selectedModule = availableModules.find(
              (module) => module.key === key
            );
            if (selectedModule?.disabled) {
              return;
            }
            const modules = [...field.value, key];
            field.onChange(modules);
          };

          const handleRemoveItem = (key: string) => {
            const modules = field.value.filter(
              (module: string) => module !== key
            );
            field.onChange(modules);
          };

          return (
            <TransferList
              availableItems={availableModules}
              selectedItems={selectedValues}
              onAddItem={handleAddItem}
              onRemoveItem={handleRemoveItem}
            />
          );
        }}
      />
    </ConfigAccordionHeader>
  );
};
